// Wallet — DATOS REALES, calculados por el backend desde los libros contables:
//   • Afiliado     → sus aportes, su crédito vigente, su participación y sus movimientos.
//   • Cooperativa  → disponible, cartera, provisión, aportes sociales y fondos del Art. 76.
//   • Federación / global → consolida las cooperativas de su alcance.
// No hay «excedentes individuales en efectivo»: no serían legales (ver migración 002).

import React, { useMemo } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import EncabezadoOperativo from '../components/EncabezadoOperativo';
import TablaDatos from '../components/TablaDatos';
import useCargar from '../hooks/useCargar';
import useCooperativaOperativa from '../hooks/useCooperativaOperativa';
import useRedReal from '../hooks/useRedReal';
import { useAuth } from '../context/AuthContext';
import { contabilidadApi, organizacionApi } from '../services/coopnetApi';
import { fecha, moneda } from '../utils/formato';

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 2 });
const Cargando = () => <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
const Cifra = ({ titulo, valor, color = '#0D47A1', nota }) => (
  <Card><CardContent>
    <Typography variant="h6" fontWeight="bold" sx={{ color }}>{valor}</Typography>
    <Typography variant="body2">{titulo}</Typography>
    {nota && <Typography variant="caption" color="text.secondary">{nota}</Typography>}
  </CardContent></Card>
);

const fondosTotal = (w) => (w.fondoLiquidezAuditoria || 0) + (w.fondoEducacion || 0) + (w.fondoSolidaridad || 0) + (w.fondoRevalorizacionAportes || 0);

// ── Afiliado ──
function WalletAfiliado({ afiliadoId }) {
  const { datos: w, cargando, error } = useCargar(() => (afiliadoId ? organizacionApi.walletAfiliado(afiliadoId) : null), [afiliadoId]);
  if (!afiliadoId) return <Alert severity="info">Tu usuario aún no está vinculado a un afiliado.</Alert>;
  if (cargando) return <Cargando />;
  if (error) return <Alert severity="error">{error}</Alert>;
  const s = w?.saldos || {}; const r = w?.revalorizacion || {};
  return (
    <>
      <Box sx={{ ...grid(230), mb: 3 }}>
        <Cifra titulo="Mis aportes sociales" valor={moneda(s.aportesSociales)} color="#2E7D32" />
        <Cifra titulo="Saldo de crédito vigente" valor={moneda(s.saldoCreditoVigente)} color="#E65100" />
        <Cifra titulo="Participación estimada en la revalorización" valor={moneda(r.participacionEstimada)} nota={`Fondo de revalorización: ${moneda(r.fondoRevalorizacionAportes)}`} />
      </Box>
      <Typography variant="h6" gutterBottom>Mis movimientos</Typography>
      <TablaDatos
        filas={w?.movimientos || []}
        claveFila={(m) => m.comprobanteId}
        vacio="Aún no tienes movimientos."
        columnas={[
          { titulo: 'Fecha', render: (m) => fecha(m.fecha) },
          { titulo: 'Comprobante', render: (m) => m.numero },
          { titulo: 'Concepto', render: (m) => m.concepto },
          { titulo: 'Tipo', render: (m) => m.tipoParaAfiliado },
          { titulo: 'Débito', align: 'right', render: (m) => (m.debito ? moneda(m.debito) : '') },
          { titulo: 'Crédito', align: 'right', render: (m) => (m.credito ? moneda(m.credito) : '') },
        ]}
      />
    </>
  );
}

// ── Una cooperativa ──
function WalletCooperativa({ id }) {
  const { datos, cargando, error } = useCargar(async () => {
    const [wallet, excedentes] = await Promise.all([contabilidadApi.wallet(id).catch((e) => { if (e.status === 404) return null; throw e; }), contabilidadApi.excedentesAnuales(id).catch(() => [])]);
    return { wallet, excedentes };
  }, [id]);
  if (cargando) return <Cargando />;
  if (error) return <Alert severity="error">{error}</Alert>;
  const w = datos?.wallet;
  if (!w) return <Alert severity="info">Esta cooperativa aún no tiene movimientos contables.</Alert>;
  return (
    <>
      <Box sx={{ ...grid(230), mb: 3 }}>
        <Cifra titulo="Disponible (caja y bancos)" valor={moneda(w.disponible)} color="#2E7D32" />
        <Cifra titulo="Cartera de créditos" valor={moneda(w.carteraCreditosBruta)} nota={`Provisión: ${moneda(w.provisionCartera)}`} />
        <Cifra titulo="Aportes sociales" valor={moneda(w.aportesSocialesTotales)} color="#6A1B9A" />
      </Box>
      <Typography variant="h6" gutterBottom>Fondos (Art. 76)</Typography>
      <Box sx={{ ...grid(230), mb: 3 }}>
        <Cifra titulo="Liquidez y auditoría" valor={moneda(w.fondoLiquidezAuditoria)} color="#E65100" />
        <Cifra titulo="Educación" valor={moneda(w.fondoEducacion)} color="#E65100" />
        <Cifra titulo="Solidaridad" valor={moneda(w.fondoSolidaridad)} color="#E65100" />
        <Cifra titulo="Revalorización de aportes" valor={moneda(w.fondoRevalorizacionAportes)} color="#E65100" />
      </Box>
      <Typography variant="h6" gutterBottom>Excedentes por año</Typography>
      <TablaDatos filas={datos.excedentes} claveFila={(x) => x.anio} vacio="Aún no hay ejercicios cerrados."
        columnas={[{ titulo: 'Año', render: (x) => x.anio }, { titulo: 'Excedente', align: 'right', render: (x) => moneda(x.excedenteDelAno) }]} />
    </>
  );
}

// ── Consolidado (federación / global) ──
function WalletConsolidado() {
  const op = useCooperativaOperativa();
  const red = useRedReal();
  const { user, contexto } = useAuth();
  const coops = useMemo(() => op.opciones, [op.opciones]);
  const { datos, cargando } = useCargar(async () => {
    const r = await Promise.allSettled(coops.map((c) => contabilidadApi.wallet(c.id)));
    return coops.map((c, i) => ({ coop: c, wallet: r[i].status === 'fulfilled' ? r[i].value : null }));
  }, [coops.map((c) => c.id).join(',')], []);

  if (red.cargando || cargando) return <Cargando />;
  if (coops.length === 0) return <Alert severity="info">Aún no hay cooperativas activas en tu alcance.</Alert>;
  const con = datos.filter((f) => f.wallet);
  const suma = (k) => con.reduce((t, f) => t + (f.wallet[k] || 0), 0);
  return (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        Consolidado de {con.length} de {coops.length} cooperativas{user?.nivel === 'federacion' && contexto?.federacion ? ` de ${contexto.federacion.nombre}` : ''}. Las que no tienen movimientos contables no suman.
      </Alert>
      <Box sx={{ ...grid(230), mb: 3 }}>
        <Cifra titulo="Disponible" valor={moneda(suma('disponible'))} color="#2E7D32" />
        <Cifra titulo="Cartera de créditos" valor={moneda(suma('carteraCreditosBruta'))} nota={`Provisión: ${moneda(suma('provisionCartera'))}`} />
        <Cifra titulo="Aportes sociales" valor={moneda(suma('aportesSocialesTotales'))} color="#6A1B9A" />
        <Cifra titulo="Fondos (Art. 76)" valor={moneda(con.reduce((t, f) => t + fondosTotal(f.wallet), 0))} color="#E65100" />
      </Box>
      <TablaDatos
        filas={datos}
        claveFila={(f) => f.coop.id}
        columnas={[
          { titulo: 'Cooperativa', render: (f) => f.coop.nombre },
          { titulo: 'Disponible', align: 'right', render: (f) => (f.wallet ? moneda(f.wallet.disponible) : '—') },
          { titulo: 'Cartera', align: 'right', render: (f) => (f.wallet ? moneda(f.wallet.carteraCreditosBruta) : '—') },
          { titulo: 'Aportes sociales', align: 'right', render: (f) => (f.wallet ? moneda(f.wallet.aportesSocialesTotales) : '—') },
          { titulo: 'Fondos', align: 'right', render: (f) => (f.wallet ? moneda(fondosTotal(f.wallet)) : '—') },
          { titulo: '', render: (f) => (f.wallet ? null : <Chip size="small" label="Sin movimientos" />) },
        ]}
      />
    </>
  );
}

const Wallet = () => {
  const { user, contexto } = useAuth();
  const op = useCooperativaOperativa();

  if (user?.nivel === 'afiliado') {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" gutterBottom>Mi wallet</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{contexto?.cooperativa?.nombre}</Typography>
        <WalletAfiliado afiliadoId={contexto?.afiliado?.id} />
      </Box>
    );
  }
  if (user?.nivel === 'cooperativa') {
    return (
      <EncabezadoOperativo titulo="Wallet" subtitulo="Estado financiero de la cooperativa" operativa={op}>
        <WalletCooperativa id={op.cooperativaId} />
      </EncabezadoOperativo>
    );
  }
  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Capital y wallet</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Finanzas consolidadas de las cooperativas de tu alcance</Typography>
      <WalletConsolidado />
    </Box>
  );
};

export default Wallet;
